import cx from 'classnames';
import styles from './logos-carousel.module.scss';

export type CustomerLogo = {
  file: string;
  label: string;
  width?: number;
  height?: number;
};

export type LogosCarouselProps = {
  logos: CustomerLogo[];
  logoBase: string;
  ariaLabel?: string;
  className?: string;
  imageExtension?: string;
  muted?: boolean;
  /** Solid edge-fade color; builds gradients on both sides unless fadeStart/fadeEnd are set. */
  fadeColor?: string;
  /** Left edge fade background (full CSS value). */
  fadeStart?: string;
  /** Right edge fade background (full CSS value). */
  fadeEnd?: string;
};

const logoSrc = (logoBase: string, file: string, imageExtension: string) =>
  `${logoBase}/${encodeURIComponent(file)}.${imageExtension}`;

const LogosCarousel = ({
  logos,
  logoBase,
  ariaLabel = 'Customer logos',
  className = '',
  imageExtension = 'png',
  muted = false,
  fadeColor,
  fadeStart,
  fadeEnd
}: LogosCarouselProps) => {
  const leftFade =
    fadeStart ?? (fadeColor ? `linear-gradient(to right, ${fadeColor}, transparent)` : undefined);
  const rightFade =
    fadeEnd ?? (fadeColor ? `linear-gradient(to left, ${fadeColor}, transparent)` : undefined);

  return (
    <section className={cx(styles.section, className)} aria-label={ariaLabel}>
      <div className={styles.viewport}>
        {leftFade ? (
          <div
            className={styles['fade-start']}
            style={{background: leftFade}}
            aria-hidden="true"
          />
        ) : null}
        <div className={styles.track}>
          {logos.map(({file, label, width, height}) => (
            <img
              key={file}
              className={cx(styles.logo, {[styles['logo-muted']]: muted})}
              src={logoSrc(logoBase, file, imageExtension)}
              alt={label}
              width={width}
              height={height}
              loading="lazy"
              decoding="async"
            />
          ))}
          {logos.map(({file, width, height}) => (
            <img
              key={`${file}-duplicate`}
              className={cx(styles.logo, styles['logo-duplicate'], {[styles['logo-muted']]: muted})}
              src={logoSrc(logoBase, file, imageExtension)}
              alt=""
              aria-hidden="true"
              width={width}
              height={height}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
        {rightFade ? (
          <div className={styles['fade-end']} style={{background: rightFade}} aria-hidden="true" />
        ) : null}
      </div>
    </section>
  );
};

export default LogosCarousel;
